import { createInstance } from "i18next";
import { initReactI18next } from "react-i18next/initReactI18next";
import type { InitI18NextOptions } from "./type";

export const initI18next = async ({
  defaultNS,
  fallbackLng,
  fallbackNS,
  lng,
  ns,
  resources,
  supportedLngs,
  debug = false,
}: InitI18NextOptions<unknown>) => {
  const i18nInstance = createInstance();
  await i18nInstance.use(initReactI18next).init({
    debug,
    defaultNS,
    fallbackLng,
    fallbackNS,
    interpolation: {
      escapeValue: false,
    },
    lng,
    ns,
    resources,
    supportedLngs,
  });
  return i18nInstance;
};

/**
 * Initializes i18next and returns the i18next instance along with a translation function.
 * @template T - Namespace type, Pass all allowed Namespace.
 * @template L - Pass all allowed Language.
 * @param {L} lng - The language to use.
 * @param {T} ns - The namespace to use.
 * @param {InitI18NextOptions<unknown>} initI18NextOptions - Options for initializing i18next.
 * @param {{ keyPrefix?: string }} [options] - Additional options.
 * @param {string} [options.keyPrefix] - Prefix for translation keys.
 * @returns {Promise<{ i18n: i18next.i18n, t: i18next.TFunction }>} An object containing the i18next instance and the translation function.
 * @example
 *  export function useServerSideTranslation(
    ns: AppNameSpacesKey,
    lng: AppLocales = "fa",
    options: object & { keyPrefix?: string } = {},
    ) {
      return useTranslation<AppNameSpacesKey, AppLocales>(
        lng,
        ns,
        {
          defaultNS: APP_NAME_SPACES_KEY.MAIN,
          fallbackLng: LOCALES.FA,
          lng: LOCALES.FA,
          ns: Object.values(APP_NAME_SPACES_KEY),
          resources: appLocaleResources,
          fallbackNS: APP_NAME_SPACES_KEY.MAIN,
          supportedLngs: Object.values(LOCALES),
        },
        options,
      );
    }
 */
export async function useTranslation<T, L extends string>(
  lng: L,
  ns: T,
  initI18NextOptions: InitI18NextOptions<unknown>,
  options?: { keyPrefix?: string }
) {
  const i18nextInstance = await initI18next(initI18NextOptions);
  const t = i18nextInstance.getFixedT(lng, Array.isArray(ns) ? ns[0] : ns, options?.keyPrefix);
  return {
    i18n: i18nextInstance,
    t,
  };
}
